import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectItemDetail } from '@deltek/specification-client/core/Models/projectItemDetail'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProductListingFlagForReview, SelectedManufacturerListing } from 'api/graphQL/types/product.type'
import { CategoryPanel, ProfessionalDisciplineInner } from 'models/category'
import React from 'react'
import { WorkspaceManufacturerListingItem } from 'shared/utils/workspaceUtils'
import {
    type AddProprietaryListingDetailInput, type DisplayTextFormatType,
    type ProductListingDialogFacet,
    ProductListingSelected,
    type SelectionType
} from './ProductListingDialog.types'

export interface DialogProviderProps {
    getFacetSelectedOptions: (facet: ProductListingDialogFacet) => string[]
    projectDetail?: Partial<ProjectItemDetail>
    hidePopper?: boolean
    closeSlide?: boolean
    productSelected?: ProductListingSelected[]
    handleAddProductOption?: (
        listingId: string,
        selectionType: SelectionType,
        withManufacturerOption: boolean,
        withMasterSpecOption: boolean,
        displayTextFormat: DisplayTextFormatType,
        addProprietaryListingDetail?: AddProprietaryListingDetailInput
    ) => Promise<void> | void
    handleRemoveProductOption?: (listingId: string, isBOD: boolean) => void
    handleModifyProductOption?: (
        listingId: string,
        selectionType: SelectionType
    ) => void
    selectedManufacturers?: SelectedManufacturerListing[]
    handleToggleMultipleManufacturerSelections?: (
        isBOD?: boolean,
        isRetainProperties?: boolean
    ) => void
    handleMultiSelectManufacturer?: (
        listingId: string,
        listingLevel: ListingLevel,
        firmName: string,
        productType?: string,
        specAgentProductTypeId?: number
    ) => void
    handleSelectUnselectAllManufacturers?: () => void
    productListingFlaggedForReview?: ProductListingFlagForReview[]
    handleProductAddFlagForReview?: (listingId: string, isBOD: boolean) => void
    handleProductRemoveFlagForReview?: (listingId: string) => void
    handleUpdateWorkspaceManufacturerBP?: (
        input: WorkspaceManufacturerListingItem
    ) => void
    userRoles?: string[]
    selectedListingId?: string | null
    projectProductListings?: Partial<ProjectManufacturerProductDetail>[]
    emptyElementTree?: boolean
    setHeaderPillToggle?: (isToggled: boolean) => void
    isAEAllProductsToggled?: boolean
    isProductsPage?: boolean
    handleCategoryChange?: (
        dataCategory: CategoryPanel,
        dataDiscipline: ProfessionalDisciplineInner
    ) => void
    searchListings?: Partial<ListingMatch>[]
    productSelectedListingIds?: string[]
    mainTabIndex?: number
    isInProject?: boolean
    listingType?: string[]
    selectedProductType?: string
    productTypesInProject?: string[]
}
export interface DialogContextValue {
    getFacetSelectedOptions: (facet: ProductListingDialogFacet) => string[]
    projectDetail?: Partial<ProjectItemDetail>
    hidePopper?: boolean
    slideOpen?: boolean
    handleSlideOpen?: (value: boolean) => void
    mainTabIndex?: number
    isAdvancedFiltersOpen?: boolean
    handleAdvancedFiltersOpen?: (value: boolean) => void
    listingId?: string
    handleListingId?: (value?: string) => void
    mediaIndex?: number
    handleMediaIndex?: (value?: number) => void
    firmLogoUrl?: string
    handleFirmLogo?: (value?: string) => void
    submissionStatus?: string
    handleSubmissionStatus?: (value?: string) => void
    activeTab?: string
    handleTabChange?: (value?: string) => void
    handleSlideClose?: VoidFunction
    productSelected?: ProductListingSelected[]
    handleAddProductOption?: (
        listingId: string,
        selectionType: SelectionType,
        withManufacturerOption: boolean,
        withMasterSpecOption: boolean,
        displayTextFormat: DisplayTextFormatType,
        addProprietaryListingDetail?: AddProprietaryListingDetailInput
    ) => Promise<void> | void
    handleRemoveProductOption?: (listingId: string, isBOD: boolean) => void
    handleModifyProductOption?: (
        listingId: string,
        selectionType: SelectionType
    ) => void
    handleToggleMultipleManufacturerSelections?: (
        isBOD?: boolean,
        isRetainProperties?: boolean
    ) => void
    handleMultiSelectManufacturer?: (
        listingId: string,
        listingLevel: ListingLevel,
        firmName: string,
        productType?: string,
        specAgentProductTypeId?: number
    ) => void
    handleSelectUnselectAllManufacturers?: () => void
    selectedManufacturers?: SelectedManufacturerListing[]
    productListingFlaggedForReview?: ProductListingFlagForReview[]
    handleProductAddFlagForReview?: (listingId: string, isBOD: boolean) => void
    handleProductRemoveFlagForReview?: (listingId: string) => void
    handleUpdateWorkspaceManufacturerBP?: (
        input: WorkspaceManufacturerListingItem
    ) => void
    popperListingId?: string
    setPopperListingId?: (value?: string) => void
    userRoles?: string[]
    projectProductListings?: Partial<ProjectManufacturerProductDetail>[]
    emptyElementTree?: boolean
    handleHeaderPillToggle?: (isAEAllProductsToggled: boolean) => void
    isAEAllProductsToggled?: boolean
    isProductsPage?: boolean
    setIsPTNotSelectedInFilter?: (value: boolean) => void
    isPTNotSelectedInFilter?: boolean
    setIsFamilyNotSelectedInFilter?: (value: boolean) => void
    isFamilyNotSelectedInFilter?: boolean
    handleCategoryChange?: (
        dataCategory: CategoryPanel,
        dataDiscipline: ProfessionalDisciplineInner
    ) => void
    searchListings?: Partial<ListingMatch>[]
    productSelectedListingIds?: string[]
    // Unmapped product dialog state (lifted to prevent virtualized list unmounting issues)
    isAddUnmappedOpen?: boolean
    addUnmappedListingId?: string
    addUnmappedAction?: () => void
    handleOpenUnmappedDialog?: (listingId: string, action: () => void) => void
    handleCloseUnmappedDialog?: () => void
    handleUnmappedDialogComplete?: (result?: any) => void
    isInProject?: boolean
    listingType?: string[]
    selectedProductType?: string
    productTypesInProject?: string[]
}

export const DialogContext = React.createContext({} as DialogContextValue)

export const DialogProvider: React.FC<DialogProviderProps> = ({
    getFacetSelectedOptions,
    projectDetail,
    hidePopper,
    closeSlide,
    productSelected,
    handleAddProductOption,
    handleRemoveProductOption,
    handleModifyProductOption,
    selectedManufacturers,
    handleToggleMultipleManufacturerSelections,
    handleMultiSelectManufacturer,
    handleSelectUnselectAllManufacturers,
    productListingFlaggedForReview,
    handleProductAddFlagForReview,
    handleProductRemoveFlagForReview,
    handleUpdateWorkspaceManufacturerBP,
    userRoles,
    selectedListingId,
    emptyElementTree,
    projectProductListings,
    children,
    setHeaderPillToggle,
    isAEAllProductsToggled,
    isProductsPage,
    handleCategoryChange,
    searchListings,
    productSelectedListingIds,
    mainTabIndex,
    isInProject,
    listingType,
    selectedProductType,
    productTypesInProject,
}) => {
    const [slideOpen, setSlideOpen] = React.useState<boolean>(false)
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = React.useState<
        boolean
    >(false)
    const [listingId, setListingId] = React.useState<string>()
    const [
        isPTNotSelectedInFilter,
        setIsPTNotSelectedInFilter,
    ] = React.useState<boolean>(false)
    const [
        isFamilyNotSelectedInFilter,
        setIsFamilyNotSelectedInFilter,
    ] = React.useState<boolean>(false)
    const [mediaIndex, setMediaIndex] = React.useState<number>()
    const [popperListingId, setPopperListingId] = React.useState<string>()
    const [firmLogoUrl, setFirmLogoUrl] = React.useState<string>()
    const [submissionStatus, setsubmissionStatus] = React.useState<string>()
    const [activeTab, setActiveTab] = React.useState<string>()

    // Unmapped product dialog state (lifted here to prevent virtualized list unmounting issues)
    const [isAddUnmappedOpen, setIsAddUnmappedOpen] = React.useState<boolean>(false)
    const [addUnmappedListingId, setAddUnmappedListingId] = React.useState<string>()
    const [addUnmappedAction, setAddUnmappedAction] = React.useState<() => void>()

    const handleSlideOpen = (value: boolean) => {
        if (!isAEAllProductsToggled) {
            setSlideOpen(value)
        }
    }

    const handleAdvancedFiltersOpen = (value: boolean) => {
        setIsAdvancedFiltersOpen(value)
    }

    const handleListingId = (value?: string) => {
        setListingId(value)
    }

    const handleMediaIndex = (value?: number) => {
        setMediaIndex(value)
    }

    const handleSubmissionStatus = (value?: string) => {
        setsubmissionStatus(value)
    }

    const handleFirmLogo = (value?: string) => {
        setFirmLogoUrl(value)
    }

    const handleTabChange = (value?: string) => {
        setActiveTab(value)
    }

    const handleSlideClose = () => {
        setSlideOpen(false)
        setListingId(undefined)
        setMediaIndex(undefined)
    }

    const handleHeaderPillToggle = (isToggled: boolean) => {
        if (isToggled !== isAEAllProductsToggled) {
            if (!!slideOpen) {
                handleSlideClose()
            }
            setHeaderPillToggle?.(isToggled)
        }
    }

    // Handlers for unmapped product dialog
    const handleOpenUnmappedDialog = (listingId: string, action: () => void) => {
        setAddUnmappedListingId(listingId)
        setAddUnmappedAction(() => action)
        setIsAddUnmappedOpen(true)
    }

    const handleCloseUnmappedDialog = () => {
        setIsAddUnmappedOpen(false)
        setAddUnmappedListingId(undefined)
        setAddUnmappedAction(undefined)
    }

    const handleUnmappedDialogComplete = (result?: any) => {
        // Execute the stored action after successful completion
        if (addUnmappedAction) {
            addUnmappedAction()
        }
        // Close the dialog
        setIsAddUnmappedOpen(false)
        setAddUnmappedListingId(undefined)
        setAddUnmappedAction(undefined)
    }

    const value: DialogContextValue = {
        getFacetSelectedOptions,
        projectDetail,
        hidePopper,
        slideOpen,
        handleSlideOpen,
        mainTabIndex,
        isAdvancedFiltersOpen,
        handleAdvancedFiltersOpen,
        listingId,
        handleListingId,
        productSelected,
        handleAddProductOption,
        handleRemoveProductOption,
        handleModifyProductOption,
        selectedManufacturers,
        handleToggleMultipleManufacturerSelections,
        handleMultiSelectManufacturer,
        productListingFlaggedForReview,
        handleProductAddFlagForReview,
        handleProductRemoveFlagForReview,
        handleUpdateWorkspaceManufacturerBP,
        mediaIndex,
        handleMediaIndex,
        firmLogoUrl,
        handleFirmLogo,
        submissionStatus,
        handleSubmissionStatus,
        activeTab,
        handleTabChange,
        handleSlideClose,
        popperListingId,
        setPopperListingId,
        userRoles,
        projectProductListings,
        emptyElementTree,
        handleHeaderPillToggle,
        isAEAllProductsToggled,
        isProductsPage,
        isPTNotSelectedInFilter,
        setIsPTNotSelectedInFilter,
        isFamilyNotSelectedInFilter,
        setIsFamilyNotSelectedInFilter,
        handleCategoryChange,
        searchListings,
        productSelectedListingIds,
        handleSelectUnselectAllManufacturers,
        // Unmapped product dialog
        isAddUnmappedOpen,
        addUnmappedListingId,
        addUnmappedAction,
        handleOpenUnmappedDialog,
        handleCloseUnmappedDialog,
        handleUnmappedDialogComplete,
        isInProject,
        listingType,
        selectedProductType,
        productTypesInProject,
    }
    React.useEffect(() => {
        if (closeSlide) {
            handleSlideClose()
        }
    }, [closeSlide])

    React.useEffect(() => {
        if (selectedListingId) {
            setListingId(selectedListingId)
            setSlideOpen(true)
            setMediaIndex(0)
        } else {
            setListingId(undefined)
            setSlideOpen(false)
        }
    }, [selectedListingId])

    return (
        <DialogContext.Provider value={value}>
            {children}
        </DialogContext.Provider>
    )
}
